import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Button, Popover, Segmented, Skeleton, Switch, Tooltip } from 'antd';
import { LuAlertTriangle, LuArrowRight, LuBell, LuBellOff, LuCheck } from 'react-icons/lu';
import { ACAO_DO_AVISO, IAviso, avisosDe, historicoDeAvisos, marcarTodosVistos, marcarVisto, meusAvisos, oQueFazer, rotaDoAlvo, rotaDoRelatorioDoAviso } from '../api/avisos';
import './avisos.css';

const quando = (d: string) => new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/** Onde a pessoa está: o botão para o mesmo lugar não aparece (e o produto é procurado ali mesmo). */
type Pagina = 'relatorio' | 'compra' | 'lote' | null;

/** Um aviso: onde mudou, o que mudou, o que fazer e os atalhos (que já levam ao produto). */
function CartaoDoAviso({ a, onAbrir, onVisto, pagina = null }: { a: IAviso; onAbrir: (rota: string) => void; onVisto?: () => void; pagina?: Pagina }) {
    const primeiro = a.itens[0]?.codigo;
    const naPropriaCompra = pagina === 'compra';
    const noProprioRelatorio = pagina === 'relatorio';
    /** O código do produto leva até ele: na compra (se é aviso de compra e não estamos no relatório) ou no relatório. */
    const rotaDoProduto = (codigo: number) => (a.tipo === 'compra' && !noProprioRelatorio ? rotaDoAlvo(a, codigo) : rotaDoRelatorioDoAviso(a, codigo));
    return (
        <div className={`aviso-mudanca${a.vistoEm ? ' is-visto' : ''}`}>
            <div className="aviso-mudanca-topo">
                <LuAlertTriangle className="aviso-mudanca-icone" />
                <div>
                    <strong>
                        {a.tipo === 'compra' ? `Compra “${a.alvoNome}” mudou depois do envio` : `Lote “${a.alvoNome}” mudou depois de gerado`}
                    </strong>
                    <span className="aviso-mudanca-sub">
                        Relatório “{a.tituloRelatorio}” · {a.porNome || 'alguém'} em {quando(a.createdAt)} · situação: {a.situacao}
                        {a.vistoEm && <> · <b>visto em {quando(a.vistoEm)}</b></>}
                    </span>
                </div>
            </div>
            <ul className="aviso-mudanca-itens">
                {a.itens.map((i, k) => (
                    <li key={k}>
                        <span className={`aviso-acao aviso-acao--${i.acao}`}>{ACAO_DO_AVISO[i.acao] || i.acao}</span>
                        <button type="button" className="aviso-produto" onClick={() => onAbrir(rotaDoProduto(i.codigo))} title="Ir até o produto">
                            <b>{i.codigo}</b> {i.descricao}
                        </button>
                        {i.detalhe ? ` · ${i.detalhe}` : ''}
                    </li>
                ))}
            </ul>
            <p className="aviso-mudanca-fazer">{oQueFazer(a)}</p>
            <div className="aviso-mudanca-acoes">
                {!naPropriaCompra && !(a.tipo === 'lote' && pagina === 'lote') && (
                    <Button type="primary" onClick={() => onAbrir(rotaDoAlvo(a, primeiro))}>
                        {a.tipo === 'compra' ? 'Abrir a compra no produto' : 'Abrir os lotes'} <LuArrowRight />
                    </Button>
                )}
                {!noProprioRelatorio && <Button onClick={() => onAbrir(rotaDoRelatorioDoAviso(a, primeiro))}>Abrir o relatório no produto</Button>}
                {onVisto && !a.vistoEm && <Button type="text" icon={<LuCheck />} onClick={onVisto}>Marcar como visto</Button>}
            </div>
        </div>
    );
}

const CHAVE_MOSTRADOS = 'rico:avisos-ja-mostrados';
/** Se a pessoa quer o aviso pequeno no canto quando chega mudança nova (o sininho conta sempre). */
const CHAVE_NO_CANTO = 'rico:avisos-no-canto';
const SEGUNDOS_NO_CANTO = 8;
const lerNoCanto = () => { try { return localStorage.getItem(CHAVE_NO_CANTO) !== 'nao'; } catch { return true; } };
const guardarNoCanto = (sim: boolean) => { try { localStorage.setItem(CHAVE_NO_CANTO, sim ? 'sim' : 'nao'); } catch { /* só não lembra */ } };
const lerMostrados = (): string[] => { try { return JSON.parse(localStorage.getItem(CHAVE_MOSTRADOS) || '[]'); } catch { return []; } };
const guardarMostrados = (ids: string[]) => { try { localStorage.setItem(CHAVE_MOSTRADOS, JSON.stringify(ids.slice(-300))); } catch { /* só não lembra */ } };

/** Resumo de uma linha do aviso, para o canto da tela. */
const resumoDoAviso = (a: IAviso) => {
    const i = a.itens[0];
    const mais = a.itens.length > 1 ? ` e mais ${a.itens.length - 1}` : '';
    const onde = a.tipo === 'compra' ? `na compra “${a.alvoNome}”` : `no lote “${a.alvoNome}”`;
    return i ? `${i.codigo} ${(ACAO_DO_AVISO[i.acao] || i.acao).toLowerCase()}${mais} ${onde}` : `Mudança ${onde}`;
};

/**
 * Central de notificações: o sininho no cabeçalho, com quantas mudanças não vistas há em
 * compras/lotes em que a pessoa trabalha (produto adicionado/excluído/restaurado no relatório
 * depois de eles terem saído). Clicar abre a lista. Mudança nova aparece uma vez num aviso
 * pequeno no canto, sem interromper o trabalho; o que fica é o sininho, até marcar como visto.
 * Consulta de novo a cada troca de tela e a cada minuto.
 */
export function SinoDeAvisos() {
    const [avisos, setAvisos] = useState<IAviso[]>([]);
    const [aberto, setAberto] = useState(false);
    /** Aviso novo mostrado no canto (some sozinho). */
    const [noCanto, setNoCanto] = useState<IAviso[]>([]);
    const [mostrarNoCanto, setMostrarNoCanto] = useState(lerNoCanto);
    /** Aba da central: as novas (não vistas) ou o histórico (60 dias, vistas também). */
    const [aba, setAba] = useState<'novas' | 'historico'>('novas');
    const [historico, setHistorico] = useState<IAviso[] | null>(null);
    useEffect(() => {
        if (!aberto || aba !== 'historico') return;
        setHistorico(null);
        historicoDeAvisos().then(setHistorico).catch(() => setHistorico([]));
    }, [aberto, aba]);
    const location = useLocation();
    const navigate = useNavigate();

    // O aviso do canto some sozinho depois de alguns segundos (cada vez que chega um novo, conta de novo).
    useEffect(() => {
        if (!noCanto.length) return;
        const t = window.setTimeout(() => setNoCanto([]), SEGUNDOS_NO_CANTO * 1000);
        return () => window.clearTimeout(t);
    }, [noCanto]);

    const carregar = async () => {
        try {
            const lista = await meusAvisos();
            setAvisos(lista);
            const mostrados = lerMostrados();
            const novos = lista.filter(a => !mostrados.includes(a._id));
            if (novos.length) {
                guardarMostrados([...mostrados, ...novos.map(a => a._id)]);
                if (lerNoCanto()) setNoCanto(novos);
            }
        } catch { /* sem avisos não é motivo para quebrar a tela */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregar(); }, [location.pathname]);
    useEffect(() => {
        const t = window.setInterval(carregar, 60000);
        return () => window.clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const visto = async (id: string) => {
        await marcarVisto(id).catch(() => undefined);
        setAvisos(l => l.filter(a => a._id !== id));
    };
    const todos = async () => {
        await marcarTodosVistos().catch(() => undefined);
        setAvisos([]);
        setAberto(false);
    };
    const abrir = (rota: string) => { setAberto(false); navigate(rota); };

    return (
        <>
            <Popover
                open={aberto}
                onOpenChange={setAberto}
                trigger="click"
                placement="bottomRight"
                overlayClassName="central-avisos"
                title={(
                    <div className="central-avisos-cabeca">
                        <div className="central-avisos-topo">
                            <span>Notificações</span>
                            {avisos.length > 0 && <Button size="small" type="link" icon={<LuCheck />} onClick={todos}>Marcar todos como vistos</Button>}
                        </div>
                        <label className="central-avisos-opcao">
                            <Switch size="small" checked={mostrarNoCanto} onChange={v => { setMostrarNoCanto(v); guardarNoCanto(v); if (!v) setNoCanto([]); }} />
                            <span>Mostrar aviso no canto da tela quando chegar mudança nova</span>
                        </label>
                    </div>
                )}
                content={(
                    <div className="central-avisos-corpo">
                        <Segmented
                            block
                            size="small"
                            value={aba}
                            onChange={v => setAba(v as 'novas' | 'historico')}
                            options={[
                                { value: 'novas', label: `Novas${avisos.length ? ` (${avisos.length})` : ''}` },
                                { value: 'historico', label: 'Histórico (60 dias)' },
                            ]}
                        />
                        {aba === 'novas' ? (
                            avisos.length
                                ? <div className="avisos-lista">{avisos.map(a => <CartaoDoAviso key={a._id} a={a} onAbrir={abrir} onVisto={() => visto(a._id)} />)}</div>
                                : <div className="central-avisos-vazio"><LuBellOff /> Nenhuma notificação nova.</div>
                        ) : !historico ? (
                            <Skeleton active paragraph={{ rows: 4 }} />
                        ) : historico.length ? (
                            <div className="avisos-lista">
                                {historico.map(a => (
                                    <CartaoDoAviso
                                        key={a._id} a={a} onAbrir={abrir}
                                        onVisto={() => visto(a._id).then(() => setHistorico(h => (h || []).map(x => (x._id === a._id ? { ...x, vistoEm: new Date().toISOString() } : x))))}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="central-avisos-vazio"><LuBellOff /> Nenhuma notificação nos últimos 60 dias.</div>
                        )}
                    </div>
                )}
            >
                <Tooltip title={aberto ? '' : avisos.length ? `${avisos.length} notificação(ões) não vista(s)` : 'Notificações'}>
                    <button type="button" className={`sino-avisos${avisos.length ? ' tem-avisos' : ''}`} aria-label="Notificações">
                        <Badge count={avisos.length} size="small" offset={[-2, 2]}>
                            <LuBell />
                        </Badge>
                    </button>
                </Tooltip>
            </Popover>
            {noCanto.length > 0 && !aberto && (
                <div className="aviso-canto" role="status" aria-live="polite" style={{ '--aviso-canto-segundos': `${SEGUNDOS_NO_CANTO}s` } as React.CSSProperties}>
                    <LuAlertTriangle className="aviso-canto-icone" />
                    <div className="aviso-canto-textos">
                        {/* Compra e lote nunca no mesmo texto: uma linha para cada tipo que chegou. */}
                        {(['compra', 'lote'] as const).map(tipo => {
                            const doTipo = noCanto.filter(a => a.tipo === tipo);
                            if (!doTipo.length) return null;
                            const titulo = tipo === 'compra'
                                ? (doTipo.length === 1 ? 'Mudança numa compra já enviada' : `${doTipo.length} mudanças em compras já enviadas`)
                                : (doTipo.length === 1 ? 'Mudança num lote de remarcação já gerado' : `${doTipo.length} mudanças em lotes de remarcação já gerados`);
                            return (
                                <div key={tipo} className="aviso-canto-tipo">
                                    <strong>{titulo}</strong>
                                    <span>{resumoDoAviso(doTipo[0])}</span>
                                </div>
                            );
                        })}
                        <div className="aviso-canto-acoes">
                            <Button size="small" className="aviso-canto-ver" onClick={() => { setNoCanto([]); setAberto(true); }}>Ver</Button>
                            <Button size="small" type="text" onClick={() => setNoCanto([])}>Fechar</Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/**
 * Faixa de aviso dentro da tela (relatório, compra ou lote): UMA linha com o resumo das
 * mudanças não vistas feitas depois que a compra/o lote saiu. "Detalhes" abre os cartões
 * (com os atalhos para o produto); as já vistas não aparecem aqui (ficam no histórico).
 */
export function FaixaDeAvisos({ tipo, id }: { tipo: 'relatorio' | 'compra' | 'lote'; id: string | null | undefined }) {
    const pagina: Pagina = tipo;
    const [avisos, setAvisos] = useState<IAviso[]>([]);
    const [aberta, setAberta] = useState(false);
    const navigate = useNavigate();
    const carregar = () => { if (id) avisosDe(tipo, id).then(l => setAvisos(l.filter(a => !a.vistoEm))).catch(() => undefined); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregar(); const t = window.setInterval(carregar, 60000); return () => window.clearInterval(t); }, [tipo, id]);
    if (!avisos.length) return null;

    const itens = avisos.flatMap(a => a.itens);
    // Um relatório só tem compra (estoque ideal) ou só lote (remarcação): o texto segue o tipo dele.
    const destino = avisos[0].tipo === 'compra' ? 'para a compra' : 'para o lote';
    const resumo = itens.slice(0, 3).map(i => `${i.codigo} ${(ACAO_DO_AVISO[i.acao] || i.acao).toLowerCase()}`).join(' · ')
        + (itens.length > 3 ? ` · +${itens.length - 3}` : '');
    const todasVistas = async () => {
        await Promise.all(avisos.map(a => marcarVisto(a._id).catch(() => undefined)));
        setAvisos([]);
    };

    return (
        <div className={`faixa-avisos${aberta ? ' is-aberta' : ''}`}>
            <div className="faixa-avisos-linha">
                <LuAlertTriangle className="faixa-avisos-icone" />
                <span className="faixa-avisos-texto">
                    <b>{itens.length} mudança(s) levada(s) {tipo === 'relatorio' ? destino : 'do relatório'}</b>
                    <span className="faixa-avisos-resumo">{resumo}</span>
                </span>
                <Button size="small" type="text" onClick={() => setAberta(a => !a)}>{aberta ? 'Ocultar' : 'Detalhes'}</Button>
                <Button size="small" type="text" icon={<LuCheck />} onClick={todasVistas}>Marcar como vistas</Button>
            </div>
            {aberta && (
                <div className="avisos-lista faixa-avisos-detalhes">
                    {avisos.map(a => (
                        <CartaoDoAviso
                            key={a._id} a={a} pagina={pagina} onAbrir={rota => navigate(rota)}
                            onVisto={() => marcarVisto(a._id).then(carregar).catch(() => undefined)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
